import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios"; 
import APIURL from "../../common/Config";
import "../blog/Blog.css";
import Header from "../header/header";
import Footer from "../footer/footer";

const slugify = (text) =>
  (text ?? "")
    .toString()
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9 -]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");

const formatScheduleDate = (numDate) => {
  if (!numDate) return "Invalid date";
  const str = String(numDate);
  if (str.length !== 8) return "Invalid date";
  const year = str.slice(0, 4);
  const month = str.slice(4, 6);
  const day = str.slice(6, 8);
  return `${day}-${month}-${year}`;
};

const Blog1 = () => {
  const { slug } = useParams();
  const [blogContent, setBlogContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [seoMetaTags, setSeoMetaTags] = useState([]);

  const parseMetaTags = (metaTagsArray) => {
    const parsedTags = [];
    let titleFromMeta = "";

    metaTagsArray.forEach((tagString) => {
      if (tagString.includes("<title>")) {
        const match = tagString.match(/<title>(.*?)<\/title>/);
        if (match && match[1]) {
          titleFromMeta = match[1];
        }
      } else if (tagString.includes("<meta")) {
        const nameMatch = tagString.match(/name="([^"]*)"/);
        const propertyMatch = tagString.match(/property="([^"]*)"/);
        const contentMatch = tagString.match(/content="([^"]*)"/);

        const name = nameMatch ? nameMatch[1] : null;
        const property = propertyMatch ? propertyMatch[1] : null;
        const content = contentMatch ? contentMatch[1] : "";

        if (name || property) {
          parsedTags.push({
            type: "meta",
            name: name,
            property: property,
            content: content,
          });
        }
      }
    });
    return { title: titleFromMeta, metaTags: parsedTags };
  };

  useEffect(() => {
    const fetchBlogBySlug = async () => {
      setLoading(true);
      setError(null);

      const cacheKey = `blogDetail-${slug}`;

      try {
        const cachedItem = localStorage.getItem(cacheKey);
        if (cachedItem) {
          const { data, timestamp, seoMetaTags: cachedSeoMetaTags } =
            JSON.parse(cachedItem);
          const now = new Date().getTime();
          const cacheDuration = 30 * 60 * 1000;

          if (now - timestamp < cacheDuration) {
            console.log("Using cached blog detail for:", slug);
            setBlogContent(data);
            setSeoMetaTags(cachedSeoMetaTags);
            setLoading(false);
            return;
          } else {
            console.log("Cached blog detail expired for:", slug);
            localStorage.removeItem(cacheKey);
          }
        }
      } catch (e) {
        console.error("Error reading from localStorage cache:", e);
        localStorage.removeItem(cacheKey);
      }

      
      let retries = 0;
      const MAX_DELAY_MS = 60 * 1000; 

      while (true) {
        try {
          const todayFormatted = new Date()
            .toISOString()
            .split("T")[0]
            .replace(/-/g, "");

          
          const responseBlogList = await axios.get(
            `${APIURL}/astroblogs/by-date?startDate=20250101&endDate=${todayFormatted}`
          );

          
          if (responseBlogList.status !== 200) {
            throw new Error(
              `Failed to fetch blog list: Status ${responseBlogList.status}`
            );
          }

          const blogList = responseBlogList.data;
          const matchedBlog = blogList.find(
            (blog) => slugify(blog.heading) === slug
          );

          if (!matchedBlog) {
            
            setLoading(false);
            setError("Blog not found.");
            return; 
          }

          
          const blogResponse = await axios.get(
            `${APIURL}/astroblogs/getBlogWithImages/${matchedBlog.id}`
          );

         
          if (blogResponse.status !== 200) {
            throw new Error(
              `Failed to fetch blog content: Status ${blogResponse.status}`
            );
          }

          const blog = blogResponse.data.blog;
          const images = blogResponse.data.images || [];

          const imageMap = {};
          images.forEach((img) => {
            const fileName = decodeURIComponent(img.contentKey.trim());
            imageMap[fileName] = img.contentUrl;
          });

          let updatedHtml = blog.htmlContent || "";
          updatedHtml = updatedHtml.replace(
            /<img[^>]+src="([^">]+)"[^>]*>/gi,
            (match, srcValue) => {
              const fileName = decodeURIComponent(srcValue.trim().split("/").pop());
              const newUrl = imageMap[fileName];
              return newUrl
                ? match.replace(/src="[^"]*"/, `src="${newUrl}"`)
                : match;
            }
          );

          const contentToCache = {
            ...blog,
            htmlContent: updatedHtml,
          };

          let currentParsedMetaTags = [];
          let currentDocumentTitle = blog.mainHeading;

          if (blog.metaTags && blog.metaTags.length > 0) {
            const { title, metaTags } = parseMetaTags(blog.metaTags);
            if (title) {
              currentDocumentTitle = title;
            }
            currentParsedMetaTags = metaTags;
          }

          const defaultMetaTags = [
            {
              name: "description",
              content: blog.mainDescription || "Default description if not provided",
            },
            { name: "keywords", content: blog.heading || "default, keywords, blog" },
            { name: "author", content: blog.article || "Heavenly Expectation Private Limited" },
            { name: "revisit-after", content: "30 days" },
            { name: "distribution", content: "global" },
            { name: "rating", content: "general" },
          ];

          defaultMetaTags.forEach((defaultTag) => {
            const isAlreadyPresent = currentParsedMetaTags.some(
              (tag) =>
                (tag.name === defaultTag.name && defaultTag.name) ||
                (tag.property === defaultTag.property && defaultTag.property)
            );
            if (!isAlreadyPresent) {
              currentParsedMetaTags.push(defaultTag);
            }
          });

          setBlogContent(contentToCache);
          setSeoMetaTags(currentParsedMetaTags);
          document.title = currentDocumentTitle;

          localStorage.setItem(
            cacheKey,
            JSON.stringify({
              data: contentToCache,
              timestamp: new Date().getTime(),
              seoMetaTags: currentParsedMetaTags,
            })
          );
          setLoading(false);
          return; 
        } catch (err) {
          console.error("Error fetching blog by slug:", err);
          setError(err.message);
          retries++;
          
          const delay = Math.min(1000 * Math.pow(2, retries - 1), MAX_DELAY_MS);
          console.warn(
            `Attempt ${retries} failed. Retrying in ${delay / 1000}s...`
          );
          await new Promise((resolve) => setTimeout(resolve, delay));
        }
      }
     
    };

    fetchBlogBySlug();
  }, [slug]);

  useEffect(() => {
    if (!loading && blogContent && seoMetaTags.length > 0) {
      const setOrCreateMetaTag = (tagData) => {
        let metaTag;
        if (tagData.name) {
          metaTag = document.querySelector(`meta[name="${tagData.name}"]`);
        } else if (tagData.property) {
          metaTag = document.querySelector(
            `meta[property="${tagData.property}"]`
          );
        }

        if (metaTag) {
          metaTag.setAttribute("content", tagData.content);
        } else {
          metaTag = document.createElement("meta");
          if (tagData.name) {
            metaTag.setAttribute("name", tagData.name);
          } else if (tagData.property) {
            metaTag.setAttribute("property", tagData.property);
          }
          metaTag.setAttribute("content", tagData.content);
          document.head.appendChild(metaTag);
        }
      };

      seoMetaTags.forEach((tag) => {
        setOrCreateMetaTag(tag);
      });
    }
  }, [loading, blogContent, seoMetaTags]);

  if (loading)
    return (
      <div
        className="loader-container"
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          flexDirection: "column",
        }}
      >
        <div
          className="spinner"
          style={{
            border: "4px solid rgba(0, 0, 0, 0.1)",
            borderTop: "4px solid #3498db",
            borderRadius: "50%",
            width: "50px",
            height: "50px",
            animation: "spin 1s linear infinite",
          }}
        />
        <p style={{ marginTop: "10px", fontSize: "1.2em", color: "#555" }}>
          Loading blog content...
        </p>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  if (error) return <p>Error loading blog content: {error}</p>;

  const { mainHeading, mainImageUrl, scheduleDate, article, htmlContent } =
    blogContent || {};

  return (
    <div>
      <Header />
      <div className="blogdetail">
        <h2>{mainHeading}</h2>
        <p>
          Published on {formatScheduleDate(scheduleDate)} by {article}
        </p>
        <div dangerouslySetInnerHTML={{ __html: htmlContent }} />
      </div>
      <Footer />
    </div>
  );
};

export default Blog1;

